import "../../../../App.scss";
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { appContext } from "../../../../AppContext";
import Details from "./Details";
import DataPermissions from "./DataPermissions";
import Users from "./Users";
import AppPermissions, { IPermission } from "./AppPermissions";
import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { INewRole, IPermissions, IdentityProvider, Role } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { IRolesPostModel, IRolesPutRequest } from '@iconics/providers.fadx-platform-api-client-v2';
import { Helper } from "./Helper";
import { IRouterProps, withRouting } from "@iconics/components";
import { TabItem, Tabs } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, DeleteButton, GridContainer, GridItem, LayoutContainer, LayoutContent, LayoutFooter, LeftButtons, RightButtons } from "@iconics/components";
import { TypeHelper } from "@iconics/core";
import { SecurityProviderId_Aad, SecurityProviderId_Auth0, SecurityProviderId_B2C, SecurityProviderId_Native, SecurityProviderId_Okta } from "../../../../Providers.StaticData/StaticDataProvider";

class RolesDetails extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private helper = new Helper();
    private labels = appContext().labels;

    constructor(props: IProps)
    {
        super(props);

        this.state = {
            canUpdate: false,
            canDelete: appContext().localStorageProvider.hasRight("DATAMODEL.Roles.Delete"),
            userId: null,
            groupId: '',
            groupError: '',
            groupDisabled: false,
            identityProviderId: 0,
            identityProviders: [],
            identityProviderError: '',
            name: '',
            nameDisabled: false,
            nameError: '',
            displayName: '',
            isOwner: false,
            availablePermissions: {},
            selectedPermissions: {},
            permissions: [],
            expandedNodeIds: [],
            showAppPermissionsDialog: false,
            etag: '',
            selectedTab: undefined,
        };
    }

    private get roleId(): string
    {
        return this.props.params.id ?? '';
    }

    private get isNew(): boolean
    {
        return (this.roleId === '0');
    }

    private get identityProvider(): IdentityProvider | undefined
    {
        const identityProvider = this.state.identityProviders.find(i => i.Id == this.state.identityProviderId);
        return identityProvider;
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Role_PageTitle;
        this.handleUserIdChange();

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        this.validator
            .addInput('identityProviderId', () => this.state.identityProviderId === 0 ? '' : this.state.identityProviderId.toString(), i => this.setState({ identityProviderError: i }))
            .isRequired();

        await this.loading(async () =>
        {
            if (this.isNew)
            {
                this.setState({ canUpdate: true });
            }
            else
            {
                const role = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.get<Role>({
                    id: this.roleId,
                    select: Role,
                    tenancy: appContext().tenantId,
                });

                await this.setStateAsync({
                    name: role.Name,
                    displayName: role.DisplayName,
                    identityProviderId: role.IdentityProviderId,
                    groupId: role.GroupId,
                    isOwner: role.IsOwner,
                    selectedPermissions: role.ApplicationActions,
                    etag: role["@odata.etag"],
                    canUpdate: appContext().localStorageProvider.hasRight("DATAMODEL.Roles.Update"),
                });
            }

            const availableRights = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.availableRights.get<IPermissions>({ tenancy: appContext().tenantId });

            const identityProvidersFilter = (!this.isNew ? `Id eq ${this.state.identityProviderId}` : `SecurityProviderTypeId eq ${SecurityProviderId_Native} or SyncType eq 'None' or (SecurityProviderTypeId eq ${SecurityProviderId_Aad} and SyncType eq 'GraphAPI') or (SecurityProviderTypeId eq ${SecurityProviderId_B2C} and SyncType eq 'GraphAPI') or (SecurityProviderTypeId eq ${SecurityProviderId_Okta} and SyncType eq 'GroupsClaimSync') or (SecurityProviderTypeId eq ${SecurityProviderId_B2C} and SyncType eq 'GroupsClaimSync') or (SecurityProviderTypeId eq ${SecurityProviderId_Auth0} and SyncType eq 'GroupsClaimSync')`);

            const identityProviders = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.identityProviders.get<PagedResponse<IdentityProvider[]>>({
                select: IdentityProvider,
                tenancy: appContext().tenantId,
                filter: identityProvidersFilter,
            });

            await this.setStateAsync({
                userId: new URLSearchParams(this.props.location.search).get('user-id'),
                availablePermissions: availableRights,
                identityProviders: identityProviders.value,
            });
        });

        try
        {
            const permissions = this.helper.toFlatPermissions(this.state.availablePermissions, this.state.selectedPermissions);
            this.setState({ permissions: permissions });
        }
        catch
        {
            this.setState({ permissions: [] });
        }
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        if (prevState.identityProviderId !== this.state.identityProviderId || prevState.identityProviders !== this.state.identityProviders)
        {
            await this.handleIdentityProviderChange();
        }
        if (prevState.userId !== this.state.userId)
        {
            this.handleUserIdChange();
        }
    }

    private async handleIdentityProviderChange(): Promise<void>
    {
        const securityProviderId = this.identityProvider?.SecurityProviderTypeId;
        const syncType = this.identityProvider?.SyncType;

        this.setState({
            nameDisabled: (
                (securityProviderId === SecurityProviderId_Aad && syncType === 'SCIMv2') ||
                (securityProviderId === SecurityProviderId_Okta && syncType === 'SCIMv2') ||
                (securityProviderId === SecurityProviderId_B2C && syncType === 'GroupsClaimSync')
            ),
            groupDisabled: (
                (syncType === 'None') ||
                (securityProviderId === SecurityProviderId_Aad && syncType === 'SCIMv2') ||
                (securityProviderId === SecurityProviderId_Okta && syncType === 'SCIMv2') ||
                (securityProviderId === SecurityProviderId_B2C && syncType === 'GroupsClaimSync')
            ),
        });
    }

    private handleIdentityProviderIdChange(value: number): void
    {
        this.setState({ identityProviderId: value }, () => this.validator.validate('identityProviderId'));
    }

    private handleUserIdChange(): void
    {
        this.backUrl = this.state.userId ? `/security-users/userdetails/${this.state.userId}` : '/security-roles';
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.delete({
                id: this.roleId,
                tenancy: appContext().tenantId,
            });
            this.props.navigate(this.backUrl);
        });
    }

    private handleCancel(): void
    {
        this.props.navigate(this.backUrl);
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            await (this.isNew ? this.createRole() : this.updateRole());
            this.props.navigate(this.backUrl);
        });
    }

    private async createRole(): Promise<void>
    {
        const { name, displayName, groupId, identityProviderId, isOwner } = this.state;

        const payload: IRolesPostModel = {
            Name: name,
            DisplayName: displayName,
            GroupId: groupId,
            ApplicationActions: JSON.stringify(this.helper.toComplexPermissions(this.state.permissions.filter(i => i.isSelected))),
            IdentityProviderId: identityProviderId,
            IsOwner: isOwner ? 1 : 0
        };

        const newRole = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.post<INewRole>({
            tenancy: appContext().tenantId,
            body: payload,
        });

        this.setState({
            name: newRole.Name,
            identityProviderId: newRole.IdentityProviderId,
            groupId: newRole.GroupId,
            isOwner: TypeHelper.toBoolean(newRole.IsOwner) ?? false,
            selectedPermissions: JSON.parse(newRole.ApplicationActions || '{}') as IPermissions,
            etag: newRole['@odata.etag'],
            canUpdate: appContext().localStorageProvider.hasRight("DATAMODEL.Roles.Update"),
        });
    }

    private async updateRole(): Promise<void>
    {
        const { name, displayName, groupId, identityProviderId, isOwner, etag } = this.state;

        const payload: IRolesPutRequest = {
            Name: name,
            DisplayName: displayName,
            GroupId: groupId,
            ApplicationActions: JSON.stringify(this.helper.toComplexPermissions(this.state.permissions.filter(i => i.isSelected))),
            IdentityProviderId: identityProviderId,
            IsOwner: isOwner ? 1 : 0,
            "@odata.etag": etag,
        };

        await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.put({
            id: this.roleId,
            body: payload,
            tenancy: appContext().tenantId,
        });
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <GridContainer>
                            <GridItem>
                                <Tabs
                                    defaultValue="0"
                                    value={this.state.selectedTab}
                                    onValueChange={param => this.setState({ selectedTab: param.value })}
                                >
                                    <TabItem
                                        title={this.labels.Platform_Role_DetailsTitle}
                                        value="0"
                                    />
                                    <TabItem
                                        title={this.labels.Platform_Role_AppActionsTitle}
                                        value="1"
                                    />
                                    {
                                        this.roleId !== '0' &&
                                        <>
                                            <TabItem
                                                title={this.labels.Platform_Role_DataSecurityDetailsTitle}
                                                value="2"
                                            />
                                            <TabItem
                                                title={this.labels.Platform_Role_UserListTitle}
                                                value="3"
                                            />
                                        </>
                                    }
                                </Tabs>
                            </GridItem>
                            <GridItem>
                                {
                                    this.renderSelectedTab()
                                }
                            </GridItem>
                        </GridContainer>
                    </LayoutContent>
                    <LayoutFooter>
                        <ButtonBar>
                            <LeftButtons>
                                {
                                    this.roleId && this.roleId !== '0' &&
                                    <DeleteButton
                                        disabled={!this.state.canDelete}
                                        dialogMessage={this.labels.Platform_Role_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDelete()}
                                    />
                                }
                            </LeftButtons>
                            <RightButtons>
                                <Button
                                    styleType="outlined"
                                    onClick={() => this.handleCancel()}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    disabled={!this.state.canUpdate || !this.validator.isValid}
                                    onClick={() => this.handleSave()}
                                >
                                    {this.labels.Platform_CommonAction_Save}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                </LayoutContainer>
            </>
        );
    }

    private renderSelectedTab(): JSX.Element
    {
        switch (this.state.selectedTab)
        {
            case "0":
            default:
                return this.renderOverview();
            case "1":
                return this.renderAppRights();
            case "2":
                return this.renderDataRights();
            case "3":
                return this.renderUsers();
        }
    }

    private renderOverview(): JSX.Element
    {
        return (
            <Details
                roleId={this.roleId}
                name={this.state.name}
                nameError={this.state.nameError}
                nameDisabled={this.state.nameDisabled}
                displayName={this.state.displayName}
                identityProviderId={this.state.identityProviderId ?? undefined}
                identityProviderError={this.state.identityProviderError}
                identityProviders={this.state.identityProviders}
                groupId={this.state.groupId ?? undefined}
                groupDisabled={this.state.groupDisabled}
                isOwner={this.state.isOwner}
                onNameChange={i => this.setState({ name: i }, () => this.validator.validate('name'))}
                onIsOwnerChange={i => this.setState({ isOwner: i })}
                onDisplayNameChange={i => this.setState({ displayName: i })}
                onIdentityProviderChange={i => this.handleIdentityProviderIdChange(i)}
                onGroupChange={i => this.setState({ groupId: i })}
            />
        );
    }

    private renderAppRights(): JSX.Element
    {
        return (
            <AppPermissions
                permissions={this.state.permissions}
                onPermissionsChange={permissions => this.setState({ permissions: permissions })}
                expandedNodeIds={this.state.expandedNodeIds}
                onNodeExpand={ids => this.setState({ expandedNodeIds: ids })}
            />
        );
    }

    private renderDataRights(): JSX.Element
    {
        return (
            <DataPermissions
                roleId={this.roleId}
                canUpdateRole={this.state.canUpdate}
            />
        );
    }

    private renderUsers(): JSX.Element
    {
        const securityProviderId = this.identityProvider?.SecurityProviderTypeId;

        return (
            <Users
                roleId={this.roleId}
                roleUsesNativeSecurityProvider={securityProviderId == SecurityProviderId_Native}
            />
        );
    }
}

export default withRouting(RolesDetails);

export interface IProps extends IRouterProps<IQueryParams> 
{
}

export interface IQueryParams
{
    id: string | undefined;
}

export interface IState
{
    canUpdate: boolean;
    canDelete: boolean;
    userId: string | null;
    groupId: string;
    groupError: string;
    groupDisabled: boolean;
    identityProviderId: number;
    identityProviderError: string;
    name: string;
    nameDisabled: boolean;
    nameError: string;
    displayName: string;
    isOwner: boolean;
    availablePermissions: IPermissions;
    selectedPermissions: IPermissions;
    permissions: IPermission[];
    expandedNodeIds: string[];
    showAppPermissionsDialog: boolean;
    identityProviders: IdentityProvider[];
    etag: string;
    selectedTab?: string;
}
